import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useSession } from "Session";

/** How long to keep a hash target aligned while the page finishes settling. */
const SETTLE_WINDOW_MS = 1000;

/**
 * How often to re-check the target's position within that window. A timer rather than
 * `requestAnimationFrame`, which is suspended while the page is hidden - a link opened in a
 * background tab would otherwise settle unaligned and stay that way.
 */
const ALIGN_INTERVAL_MS = 50;

/** Anything here means the visitor is scrolling for themselves, so alignment stands down. */
const INTERRUPT_EVENTS = ["keydown", "touchstart", "wheel"];

/**
 * Resets the scroll position on navigation, or brings the hash target into view when there is one.
 *
 * Hash links cannot be left to the browser here, for two reasons:
 *
 * - `MainRouter` renders nothing until the session check resolves, so on a deep link such as
 *   `/#features` the target does not exist yet at the moment the native jump would happen. That is
 *   why `isReady` is a dependency - it re-runs this once the page is actually mounted.
 * - Translations load asynchronously (`useSuspense: false`), so the page keeps changing height for
 *   a moment after the first paint - on the landing page it settles 130px shorter, around 170ms in.
 *   A jump made before then ends up off target, so the loop below holds the target in place until
 *   the layout stops moving, and stands down as soon as the visitor scrolls.
 */
const ScrollToTop = () => {
	const { hash, key, pathname } = useLocation();
	const { isReady } = useSession();

	useEffect(() => {
		const target = hash ? document.getElementById(decodeURIComponent(hash.slice(1))) : null;
		let timer = 0;

		const stopAligning = (): void => {
			window.clearInterval(timer);
			INTERRUPT_EVENTS.forEach((event: string) => {
				window.removeEventListener(event, stopAligning);
			});
		};

		if (!target) {
			window.scrollTo({ left: 0, top: 0 });

			return stopAligning;
		}

		const settledBy = Date.now() + SETTLE_WINDOW_MS;
		const restingTop = (): number => Math.round(target.getBoundingClientRect().top);

		target.scrollIntoView();

		// Drift rather than a comparison against zero: a section near the foot of the page cannot
		// reach the top of the viewport, and re-scrolling it every tick would achieve nothing.
		let alignedAt = restingTop();

		timer = window.setInterval(() => {
			if (Date.now() > settledBy) {
				stopAligning();

				return;
			}

			if (restingTop() !== alignedAt) {
				target.scrollIntoView();
				alignedAt = restingTop();
			}
		}, ALIGN_INTERVAL_MS);

		INTERRUPT_EVENTS.forEach((event: string) => {
			// eslint-disable-next-line ban/ban -- the visitor's own scrolling, not layout measurement
			window.addEventListener(event, stopAligning, { passive: true });
		});

		return stopAligning;
	}, [hash, isReady, key, pathname]);

	return null;
};

export default ScrollToTop;
